import React, { ElementType, forwardRef, useState } from 'react';
import { Box, BoxProps, Skeleton, SkeletonProps } from '@mui/material';
import { CircleCloseFilledIcon } from '@/src/components/icon/filled/CircleCloseFilledIcon';

export interface ImageProps extends BoxProps {
  /** Image alt message */
  alt: string;
  /** Image src path */
  src: string;
  /** Image aspect ratio */
  aspectRatio?: number;
  /**
   * The Skeleton will be displayed when the image is loading. @default true
   * If false, the Image will be rendered without Skeleton.
   */
  skeleton?: boolean;
  /** Additional props for customizing the skeleton loader. */
  skeletonProps?: SkeletonProps;
  /** Additional props for customizing the wrapper box  */
  wrapperProps?: BoxProps<ElementType>;
  /** Custom component to replace skeleton. */
  customLoader?: React.ReactNode;
  /** Image failed to load message or component. */
  errorContent?: React.ReactNode;
  /** Additional props for customizing the error display box. */
  errorProps?: BoxProps<ElementType>;
  /** Controlled loading. */
  loading?: boolean;
  /** Controlled error. */
  error?: boolean;
}

/**
 * The Image component that displays an image with a loading skeleton and optional customizable error messages or components when the image fails to load.
 * The Image component also supports a custom loader to replace the default skeleton and the skeleton property can be set to false to disable the skeleton entirely.
 */
export const Image: React.FC<ImageProps> = forwardRef(
  (
    {
      aspectRatio,
      width,
      height,
      skeleton = true,
      skeletonProps,
      wrapperProps,
      errorProps,
      customLoader,
      errorContent,
      loading,
      error,
      sx,
      children,
      ...rest
    },
    ref,
  ) => {
    const [loaded, setLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const { sx: skeletonSx, ...restSkeleton } = skeletonProps || {};
    const { sx: wrapperSx, ...restWrapper } = wrapperProps || {};
    const { sx: errorSx, ...restError } = errorProps || {};

    return (
      <Box
        data-testid="Image.wrapper"
        sx={{
          aspectRatio: aspectRatio ?? 'initial',
          width: width ?? 'auto',
          height: height ?? 'auto',
          maxWidth: '100%',
          position: 'relative',
          ...wrapperSx,
        }}
        {...restWrapper}
      >
        {!hasError && !error && !loading && (
          <Box
            component="img"
            data-testid="Image.component"
            ref={ref}
            onLoad={() => setLoaded(true)}
            onError={() => setHasError(true)}
            width={width}
            height={height}
            sx={{
              aspectRatio: aspectRatio ?? 'initial',
              objectFit: 'cover',
              width: '100%',
              height: '100%',
              display: 'block',
              ...sx,
            }}
            {...rest}
          />
        )}
        {(!loaded || loading) &&
          !hasError &&
          !error &&
          (customLoader ||
            (skeleton && (
              <Skeleton
                data-testid="Image.skeleton"
                variant="rectangular"
                sx={{ width: '100%', height: '100%', ...skeletonSx }}
                {...restSkeleton}
              />
            )))}
        {(hasError || error) && (
          <Box
            data-testid="Image.error"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              backgroundColor: 'disabled.secondary',
              ...errorSx,
            }}
            {...restError}
          >
            {errorContent || <CircleCloseFilledIcon color="error" />}
          </Box>
        )}
        {children}
      </Box>
    );
  },
);
